import React, { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Envelope, Lock, ShieldCheck } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Logomark } from '@/components/ui/Logomark'
import { useAdminAuthStore } from '../adminAuthStore'

/**
 * The only unguarded admin route. Not linked from anywhere in the tenant
 * app; the team reaches it by URL. An already-authenticated admin is sent
 * straight into the panel.
 */
export default function AdminLoginPage() {
  const status = useAdminAuthStore((s) => s.status)
  const isLoggingIn = useAdminAuthStore((s) => s.isLoggingIn)
  const error = useAdminAuthStore((s) => s.error)
  const login = useAdminAuthStore((s) => s.login)
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const from = (location.state as { from?: string } | null)?.from
  const target = from && from.startsWith('/admin') && from !== '/admin/login' ? from : '/admin/bugs'

  if (status === 'authenticated') return <Navigate to={target} replace />

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const ok = await login(email, password)
    if (ok) navigate(target, { replace: true })
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-6">
          <Logomark size={22} />
          <span className="font-semibold tracking-tight">Beevelope</span>
          <span className="ml-1 rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            Admin
          </span>
        </div>

        <h1 className="text-xl font-semibold mb-1">Team sign in</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Report &amp; Feedback panel. Team access only.
        </p>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Input
            label="Email"
            type="email"
            autoComplete="username"
            icon={<Envelope size={16} />}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Input
            label="Password"
            type="password"
            autoComplete="current-password"
            icon={<Lock size={16} />}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {error && (
            <p role="alert" className="text-[13px] text-destructive">
              {error}
            </p>
          )}
          <Button type="submit" className="w-full" loading={isLoggingIn} icon={<ShieldCheck size={16} />}>
            Sign in
          </Button>
        </form>
      </div>
    </div>
  )
}
